<script setup>
    import Layout from "@/layouts/main.vue";
    import PageHeader from "@/components/page-header";  
    import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
    import { required, helpers } from "@vuelidate/validators";
    import useVuelidate from "@vuelidate/core";
    import { computed, ref, reactive, onMounted } from 'vue';
    import { useStore } from 'vuex';
    import Multiselect from '@suadelabs/vue3-multiselect';

    const dropdownMvRegistrationTypes = computed(() => store.state.mvRegistrationType.dropdownMVRegistrationTypes);

    const state = reactive({
        id: '',
        name: '',
        number: '',
        motor_vehicle_registration_type_id: 1,
        lgus: null,
        updated_at: null
    })

    const rules = computed(() => ({
        name: {
            required: helpers.withMessage("Name is required", required),
        },
        motor_vehicle_registration_type_id: {
            required: helpers.withMessage("MV Registration ID is required", required),
        },
        number: {
            required: helpers.withMessage("Plate Number is required", required),
        },
        lgus: {
            required: helpers.withMessage("Please select at least one LGU", required),
        },
    }));

    const store = useStore();
    const v$ = useVuelidate(rules, state);

    const title = ref("LGU Whitelist");
    const items = ref([
        {
            text: "Home",
            href: "/",
        },
        {
            text: "Settings",
            href: "/settings/whitelist-lgu",
            active: true
        },
        {
            text: "Add New LGU Whitelist",
            active: true,
        }
    ]);

    const submitted = ref(false);
    const loading = computed(() => store.state.globals.loading);
    const notification = computed(() => store ? store.state.notification : null)
    const lguOptions = computed(() => store ? store.state.lgu.dropdownLGUs : []);

    const createWhiteList = async () => {
        submitted.value = true
        v$.value.$touch();

        if (v$.value.$invalid) return;
        await store.dispatch('whitelistLgu/createWhiteList', state);
    }

    onMounted(async () => {
      await store.dispatch('mvRegistrationType/getDropdownMVRegistrationTypes');
      await store.dispatch('lgu/getDropdownLGUs');
    });
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
            <b-overlay :show="loading">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Add New LGU Whitelist</h4>
                    </div>
                    <div class="card-body">
                        <div v-if="notification && notification.messages.length > 0">
                            <b-alert v-for="(message, index) in notification.messages" :key="index"
                                show variant="danger" class="fade show" role="alert">
                                {{ message }}
                            </b-alert>
                        </div>

                        <form @submit.prevent="createWhiteList" class="mx-2">
                            <div class="row">
                                <p class="fs-6 text text-decoration-underline">LGU Whitelist Information</p>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="name" class="form-label required">Name</label> 
                                        <input 
                                            autofocus
                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                            v-model="state.name" class="form-control" type="text" placeholder="Enter name..." id="name" />
                                        <div
                                            v-for="item of v$.name.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="description" class="form-label required">MV Registration Type</label> 
                                        <select 
                                            :class="{ 'is-invalid': submitted && v$.motor_vehicle_registration_type_id.$error }"
                                            class="form-select" v-model="state.motor_vehicle_registration_type_id">
                                            <option v-for="type in dropdownMvRegistrationTypes" :key="type.id" :value="type.id">
                                                {{ type.name }}
                                            </option>
                                        </select>
                                        <div
                                            v-for="item of v$.motor_vehicle_registration_type_id.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="mb-3">
                                        <label for="remarks" class="form-label required">Plate / CS / MV File #</label> 
                                        <input
                                            type="text"
                                            :class="{ 'is-invalid': submitted && v$.number.$error }"
                                            v-model="state.number" class="form-control" placeholder="Enter plate / CS / MV file #..." id="number" />
                                        <div
                                            v-for="item of v$.number.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="lguInput" class="form-label required">LGU Assign</label>
                                        <multiselect
                                            :class="{ 'is-invalid': submitted && v$.lgus.$error }"
                                            :multiple="true"
                                            v-model="state.lgus" 
                                            :close-on-select="true"
                                            :options="lguOptions"
                                            placeholder="Select LGU Assign"
                                            label="name"
                                            track-by="name"
                                        />
                                        <div
                                            v-for="item of v$.lgus.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="text-end">
                                        <router-link to="/settings/whitelist-lgu" type="submit" class="btn btn-danger mr-3">
                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                            Cancel
                                        </router-link>
                                        <button type="submit" class="btn btn-success">
                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                            Add LGU Whitelist
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </b-overlay>
        </div>
    </div>
  </Layout>
</template>
